import { useEffect, useState } from 'react';
import { SlidersHorizontal, X } from 'lucide-react';

/**
 * Visible glass tuner — small floating bar bottom-right of the viewport.
 * Lets any visitor adjust the dashboard's transparency and blur to taste.
 * Values persist in localStorage so the tuned look survives page reloads.
 *
 * Implementation: injects a runtime <style> tag with !important rules
 * targeting [data-dash-glass] so it overrides Tailwind without modifying
 * the DashboardMockup component.
 */
const STYLE_ID = 'glass-tuner-style';
const STORAGE_KEY = 'sprynai-glass-v3';
const DEFAULT_OPACITY = 60; // % of black
const DEFAULT_BLUR = 0; // px

export default function GlassTuner() {
  const [opacity, setOpacity] = useState<number>(DEFAULT_OPACITY);
  const [blur, setBlur] = useState<number>(DEFAULT_BLUR);
  const [open, setOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate from localStorage exactly once
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const v = JSON.parse(saved);
        if (typeof v.opacity === 'number') setOpacity(v.opacity);
        if (typeof v.blur === 'number') setBlur(v.blur);
      } catch {
        /* ignore parse errors */
      }
    }
    setHydrated(true);
  }, []);

  // Inject style tag with current values
  useEffect(() => {
    if (!hydrated) return;
    let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement('style');
      el.id = STYLE_ID;
      document.head.appendChild(el);
    }
    const blurStr = `blur(${blur}px)`;
    el.textContent = `[data-dash-glass]{background-color:rgba(0,0,0,${(opacity / 100).toFixed(3)}) !important;-webkit-backdrop-filter:${blurStr} !important;backdrop-filter:${blurStr} !important;}`;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ opacity, blur }));
  }, [hydrated, opacity, blur]);

  if (!hydrated) return null;

  return (
    <div className="font-sans pointer-events-auto">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Adjust dashboard glass"
          className="flex items-center gap-2 rounded-full border border-white/15 bg-black/70 backdrop-blur-md px-3 py-2 md:px-4 md:py-2.5 text-xs font-medium text-white/90 shadow-lg hover:bg-black/85 transition-colors"
        >
          <SlidersHorizontal className="h-4 w-4 md:h-3.5 md:w-3.5" />
          {/* Hide the label on mobile so the pill collapses to a compact icon button
              that fits next to the hamburger without colliding with the "New" badge. */}
          <span className="hidden md:inline">Adjust glass</span>
        </button>
      ) : (
        <div className="w-[280px] rounded-2xl border border-white/15 bg-black/85 backdrop-blur-xl p-4 text-white shadow-2xl">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-3.5 w-3.5 text-white/70" />
              <span className="text-xs font-semibold tracking-tight text-white/90">
                Adjust dashboard glass
              </span>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="text-white/50 hover:text-white"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <label className="block mb-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] uppercase tracking-wider text-white/55">
                Tint
              </span>
              <span className="text-[11px] tabular-nums text-white/90">
                {opacity}%
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={60}
              step={1}
              value={opacity}
              onChange={(e) => setOpacity(Number(e.target.value))}
              className="w-full accent-white"
            />
          </label>

          <label className="block mb-1">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] uppercase tracking-wider text-white/55">
                Blur
              </span>
              <span className="text-[11px] tabular-nums text-white/90">
                {blur}px
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={60}
              step={1}
              value={blur}
              onChange={(e) => setBlur(Number(e.target.value))}
              className="w-full accent-white"
            />
          </label>

          <button
            type="button"
            onClick={() => {
              setOpacity(DEFAULT_OPACITY);
              setBlur(DEFAULT_BLUR);
            }}
            className="mt-3 w-full px-3 py-1.5 rounded-md border border-white/15 text-[11px] text-white/70 hover:text-white hover:border-white/30 transition-colors"
          >
            Reset
          </button>
        </div>
      )}
    </div>
  );
}
